import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import Highcharts from 'highcharts';
import HC_more from 'highcharts/highcharts-more';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';
import { IndicatorCodes } from 'src/app/core/_enums/indicators.code';
import { SationImpactColor } from 'src/app/core/_enums/station.impact.color';
import { Impact } from 'src/app/core/_models/impact';
import { Indicator } from 'src/app/core/_models/indicator';
import { PdfPictures } from 'src/app/core/_models/pdfPictures';
import { ProjectEcoPro } from 'src/app/core/_models/projectEcoPro';
import { HIGHCHARTS_EXPORTING, HIGHCHARTS_TRANSLATIONS } from 'src/constants/graph.constants';
import { SvgToPngService } from '../../../core/_utils/svgToPng.service';

HC_more(Highcharts);
HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);

let componentContext: any;

@Component({
  selector: 'app-bar-indicators-global-byStations',
  templateUrl: './bar-indicators-global-byStations.component.html',
  styleUrls: ['./bar-indicators-global-byStations.component.scss']
})
export class BarIndicatorsGlobalByStationsComponent implements OnChanges, OnInit {
  @Input() projectData: ProjectEcoPro = {};
  @Input() indicators: Indicator[] = [];
  @Input() isEcoPro: boolean = true;
  @Input() synthese: any;
  chart: any;

  indicatorComparatif: Indicator = {
    isChecked: false
  };

  private defaultApplied = false;
  updateFlag: boolean = false;
  totalsRef: number[] = [];

  // Highcharts config

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {
    chart: {
      type: 'column'
    },
    lang: HIGHCHARTS_TRANSLATIONS,
    title: {
      text: 'Comparaison par scénario et module'
    },
    exporting: HIGHCHARTS_EXPORTING,
    legend: {
      align: 'right',
      x: -30,
      verticalAlign: 'top',
      y: 0,
      floating: false,
      backgroundColor: Highcharts.defaultOptions.legend!.backgroundColor || 'white',
      borderColor: '#CCC',
      borderWidth: 1,
      shadow: false
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: true
        }
      }
    }
  };

  constructor(public labelI18n: LabelI18nService) {
    componentContext = this;
  }

  ngOnChanges() {
    if (!this.defaultApplied && this.indicators.length > 0 && this.synthese?.length > 0) {
      this.defaultApplied = true;
      const coIndicator = this.indicators.find((i: Indicator) => i.code === IndicatorCodes.CO2);
      if (coIndicator) {
        coIndicator.isChecked = true;
        this.indicatorComparatif = coIndicator;
      }
    }
    if (this.indicatorComparatif) this.formatDataForGraph();
  }

  ngOnInit() {
    if (this.isEcoPro) {
      this.indicators = this.indicators.filter((row: any) => {
        return row.code === IndicatorCodes.Energie || row.code === IndicatorCodes.CO2;
      });
    }
  }

  async generateCharts(indicators: Indicator[]) {
    let pdfCharts: PdfPictures = {
      coCharts: [],
      energyCharts: [],
      gncharts: [],
      tkCharts: [],
      synthetiqueResultENcharts: [],
      synthetiqueResultCOcharts: []
    };

    var options = Highcharts.merge(Highcharts.getOptions().exporting, { type: 'application/pdf' });

    for (let indicator of indicators) {
      this.indicatorComparatif = indicator;
      this.formatDataForGraph();
      switch (indicator.code) {
        case 'EN':
          var base64 = await SvgToPngService.convertSVGToPNG(this.chart.getSVG(options), 1200, 800);
          if (base64 != null) pdfCharts.energyCharts?.push(base64);
          break;
        case 'CO':
          var base64 = await SvgToPngService.convertSVGToPNG(this.chart.getSVG(options), 1200, 800);
          if (base64 != null) pdfCharts.coCharts?.push(base64);
          break;
        case 'PR':
          var base64 = await SvgToPngService.convertSVGToPNG(this.chart.getSVG(options), 1200, 800);
          if (base64 != null) pdfCharts.gncharts?.push(base64);
          break;
        case 'TK':
          var base64 = await SvgToPngService.convertSVGToPNG(this.chart.getSVG(options), 1200, 800);
          if (base64 != null) pdfCharts.tkCharts?.push(base64);
          break;
        default:
          break;
      }
    }
    return pdfCharts;
  }

  onChangeIndicatorSelect(cb: MatCheckboxChange, indicator: Indicator) {
    this.indicators.forEach((i) => (i.isChecked = false));
    if (cb.checked) {
      indicator.isChecked = true;
      this.indicatorComparatif = indicator;
      this.formatDataForGraph();
    } else {
      this.indicatorComparatif = { isChecked: false };
    }
  }

  generateArray(obj: any) {
    return Object.keys(obj).map((key) => {
      return { key: key, value: obj[key] };
    });
  }
  formatDataForGraph() {
    let series: {
      type: string;
      name: string | undefined;
      color?: string;
      data: number[];
    }[] = [];
    const stationImpactsNames: string[] = [];
    let stationsImpacts: Impact[] = [];
    const stationsImpactsForSelectedIndicator: Impact[] = [];

    if (this.indicatorComparatif.code && this.synthese && this.synthese.length > 0) {
      let specificSynthese = this.synthese.find((row: any) => row.code === this.indicatorComparatif.code);
      specificSynthese.solutions.forEach((solution: any) => {
        let stationImpact: Impact = {};
        let headers = this.generateArray(solution.rows[0].values);

        headers.forEach((header: any) => {
          stationImpact = {};
          switch (header.key) {
            case SationImpactColor.BuildTransportCostIn.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.BuildTransportCostIn.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.BuildTransportCostIn;
              break;
            case SationImpactColor.MaterialMakingCost.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.MaterialMakingCost.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.MaterialMakingCost;
              break;
            case SationImpactColor.InitialTransportCost.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.InitialTransportCost.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.InitialTransportCost;
              break;
            case SationImpactColor.MaterialCost.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.MaterialCost.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.MaterialCost;
              break;
            case SationImpactColor.WorkingCost.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.WorkingCost.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.WorkingCost;
              break;
            case SationImpactColor.BuildTransportCostOut.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.BuildTransportCostOut.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.BuildTransportCostOut;
              break;
            case SationImpactColor.NaturalAggregates.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.NaturalAggregates.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.NaturalAggregates;
              break;
            case SationImpactColor.RecycledMaterials.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.RecycledMaterials.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.RecycledMaterials;
              break;
            case SationImpactColor.AsphaltAggregates.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.AsphaltAggregates.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.AsphaltAggregates;
              break;
            case SationImpactColor.ReusedSpoil.toString():
              stationImpact.value = parseFloat(header.value);
              stationImpact.color = SationImpactColor.ReusedSpoil.value;
              stationImpact.solutionId = solution.solutionId;
              stationImpact.scenarioSolutionAssociationId = solution.scenarioSolutionAssociationId;
              stationImpact.displayName = specificSynthese.headers.ReusedSpoil;
              break;
            default:
              break;
          }
          if (Object.keys(stationImpact).length !== 0) {
            stationsImpactsForSelectedIndicator.push(stationImpact);
          }
          if (stationImpact.displayName && !stationImpactsNames.includes(stationImpact.displayName)) {
            stationImpactsNames.push(stationImpact.displayName);
          }
        });
      });

      specificSynthese.solutions.forEach((solution: any) => {
        stationImpactsNames.forEach((stationImpactName) => {
          let stationImpactRef = stationsImpacts.find((stationImpact) => stationImpact.displayName == stationImpactName);

          if (
            stationsImpactsForSelectedIndicator.find(
              (stationImpactSelected) =>
                stationImpactSelected.displayName == stationImpactName && stationImpactSelected.solutionId == solution.solutionId && stationImpactSelected.scenarioSolutionAssociationId == solution.scenarioSolutionAssociationId
            ) == null
          ) {
            if (stationImpactRef == null) {
              stationsImpacts.push({
                displayName: stationImpactName,
                dataForGraph: [0]
              });
            } else {
              stationImpactRef.dataForGraph?.push(0);
            }
          } else {
            const stationsImpactsForStation = stationsImpactsForSelectedIndicator.filter(
              (stationImpact) => stationImpact.displayName == stationImpactName && stationImpact.solutionId == solution.solutionId && stationImpact.scenarioSolutionAssociationId == solution.scenarioSolutionAssociationId
            );

            let ceuValueForStationType = 0;

            stationsImpactsForStation.forEach((stationImpact) => {
              if (stationImpact && stationImpact.value) {
                ceuValueForStationType += stationImpact.value;
              }
            });

            if (stationImpactRef == null) {
              let stationImpactColor = stationsImpactsForSelectedIndicator.find((stationImpact) => stationImpact.displayName == stationImpactName)?.color;
              stationsImpacts.push({
                displayName: stationImpactName,
                color: stationImpactColor,
                dataForGraph: [Number(ceuValueForStationType.toFixed(2))]
              });
            } else {
              stationImpactRef.dataForGraph?.push(Number(ceuValueForStationType.toFixed(2)));
            }
          }
        });
      });
      stationsImpacts.forEach((stationImpact) => {
        if (stationImpact.dataForGraph) {
          series.push({
            type: 'column',
            name: stationImpact.displayName,
            data: stationImpact.dataForGraph,
            color: stationImpact.color
          });
        }
      });

      this.chartOptions.xAxis = {
        categories: specificSynthese.solutions.map((solution: any) => solution.displayName!)
      };

      if (this.indicatorComparatif.code == 'AE' || this.indicatorComparatif.code == 'GN' || this.indicatorComparatif.code == 'DRC' || this.indicatorComparatif.code == 'MR')
        this.chartOptions.legend = {
          enabled: false
        };
      else
        this.chartOptions.legend = {
          enabled: true
        };

      this.chartOptions.yAxis = {
        /*min: -40,*/
        title: {
          text: componentContext?.labelI18n.indicatorLabelWithUnit(componentContext?.indicatorComparatif)
        },
        stackLabels: {
          enabled: true,
          useHTML: true,
          y: -10,
          style: {
            fontWeight: 'bold',
            fontSize: '15px',
            color:
              // theme
              (Highcharts.defaultOptions.title!.style && Highcharts.defaultOptions.title!.style.color) || 'black'
          },
          formatter: function () {
            if (this.x == 0) {
              componentContext.totalRef = this.total;
              return '';
            } else {
              if (componentContext.totalRef) {
                const percentageDiff = (100 * (this.total - componentContext.totalRef)) / componentContext.totalRef;
                if (!isNaN(percentageDiff) && this.total != 0 && componentContext?.indicatorComparatif?.code != 'PR') {
                  const displayValue =
                    percentageDiff == 0
                      ? '&nbsp' + '-' + '&nbsp'
                      : percentageDiff > 0
                        ? componentContext?.indicatorComparatif?.code != 'PR'
                          ? '+' + Math.round(percentageDiff) + '%'
                          : ' '
                        : componentContext?.indicatorComparatif?.code != 'PR'
                          ? Math.round(percentageDiff) + '%'
                          : ' ';
                  const backgroundColor = percentageDiff < 0 ? '#25e043' : percentageDiff > 0 ? '#ff4c4c' : 'lightgray';

                  return '<span style="background-color:' + backgroundColor + '; font-size: 13px; padding: 3px; border:1px solid">' + displayValue + '</span>';
                } else {
                  return '';
                }
              } else {
                return '';
              }
            }
          }
        }
      };
      this.chartOptions.series = series as Highcharts.SeriesOptionsType[];
      this.updateFlag = true;
      this.chart = Highcharts.chart('hidden-container', this.chartOptions);
    }
  }
}
